/**
 * Public Report Privacy API
 * The customer's privacy policy for QR codes placed in public places
 * (config/publicReportPrivacy, molnfast-react#734).
 *
 * The customer is personuppgiftsansvarig: we store their link or text and
 * never write a policy of our own. The report page gets it through the
 * publicReportApi validateToken callable, never from the database.
 */
import { getDatabase, ref, get, set, remove } from 'firebase/database';
import { formatDateForStorage } from '../Helpers/DateUtils';
import {
    PublicReportPrivacyPolicy,
    PUBLIC_REPORT_PRIVACY_PATH,
    MAX_PRIVACY_POLICY_TEXT_LENGTH,
} from '../types/publicReport';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';

export interface PrivacyPolicyInput {
    url: string;
    text: string;
}

/**
 * Trim the input and check it. Returns the fields to store, or throws a
 * Swedish error message.
 */
export const normalizePrivacyPolicyInput = (input: PrivacyPolicyInput): Pick<PublicReportPrivacyPolicy, 'url' | 'text'> => {
    const url = input.url.trim();
    const text = input.text.trim();
    if (!url && !text) {
        throw new Error('Ange en länk eller en text');
    }
    if (url) {
        if (!url.startsWith('https://')) {
            throw new Error('Länken måste börja med https://');
        }
        try {
            new URL(url);
        } catch {
            throw new Error('Länken är inte en giltig adress');
        }
    }
    if (text.length > MAX_PRIVACY_POLICY_TEXT_LENGTH) {
        throw new Error(`Texten får vara högst ${MAX_PRIVACY_POLICY_TEXT_LENGTH} tecken`);
    }
    return { ...(url && { url }), ...(text && { text }) };
};

class PublicReportPrivacyApi {
    private database = getDatabase();

    /**
     * The stored policy, or null when none is set
     */
    async getPolicy(): Promise<PublicReportPrivacyPolicy | null> {
        const snapshot = await get(ref(this.database, PUBLIC_REPORT_PRIVACY_PATH));
        if (!snapshot.exists()) return null;
        const d = snapshot.val() as Record<string, unknown>;
        if (!d || typeof d !== 'object') return null;
        const policy: PublicReportPrivacyPolicy = {
            url: typeof d.url === 'string' ? d.url : undefined,
            text: typeof d.text === 'string' ? d.text : undefined,
            updatedBy: typeof d.updatedBy === 'string' ? d.updatedBy : undefined,
            updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : undefined,
        };
        return policy.url || policy.text ? policy : null;
    }

    /**
     * Replace the policy (whole node, so a cleared field is removed)
     */
    async savePolicy(input: PrivacyPolicyInput): Promise<PublicReportPrivacyPolicy> {
        const uid = await requireAdmin(this.database, 'spara integritetspolicyn');
        const policy: PublicReportPrivacyPolicy = {
            ...normalizePrivacyPolicyInput(input),
            updatedBy: uid,
            updatedAt: formatDateForStorage(new Date()),
        };
        await set(ref(this.database, PUBLIC_REPORT_PRIVACY_PATH), policy);
        recordActivity('settings.updated');
        return policy;
    }

    /**
     * Remove the policy. Public codes then show only the neutral notice.
     */
    async clearPolicy(): Promise<void> {
        await requireAdmin(this.database, 'ta bort integritetspolicyn');
        await remove(ref(this.database, PUBLIC_REPORT_PRIVACY_PATH));
        recordActivity('settings.updated');
    }
}

export const publicReportPrivacyApi = new PublicReportPrivacyApi();
